import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, CircleAlert, Loader2 } from 'lucide-react';
import {
  filingStatusLabel,
  findingUxCategory,
  findingUxSeverity,
  type FilingReviewFinding,
  type FilingReviewReport,
  type ReviewAction,
} from '../../lib/filingReviewTypes';
import {
  goToReviewFinding,
  runFilingReviewFindingAction,
  useLexCounselStore,
} from '../../store/lexCounselStore';
import { PANEL_BODY, PANEL_CAPTION, PANEL_MUTED } from './lexCounselPanelUi';

function statusBadgeClass(status: string): string {
  if (status === 'ready_for_filing') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  if (status === 'ready_with_minor_issues') return 'bg-amber-500/15 text-amber-800 dark:text-amber-200';
  return 'bg-rose-500/15 text-rose-700 dark:text-rose-300';
}

function riskClass(risk: string): string {
  if (risk === 'low') return 'text-emerald-700 dark:text-emerald-300';
  if (risk === 'high') return 'text-rose-700 dark:text-rose-300';
  return 'text-amber-800 dark:text-amber-200';
}

function scorecardIcon(status: string) {
  if (status === 'pass') return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />;
  if (status === 'fail') return <CircleAlert className="h-3.5 w-3.5 text-rose-600" />;
  return <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />;
}

function severityDot(sev: string) {
  const cls =
    sev === 'critical'
      ? 'bg-rose-500'
      : sev === 'warning'
        ? 'bg-amber-500'
        : 'bg-sky-500';
  return <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${cls}`} />;
}

function FindingRow({ finding }: { finding: FilingReviewFinding }) {
  const activeId = useLexCounselStore((s) => s.activeReviewFindingId);
  const sev = findingUxSeverity(finding);
  const isActive = activeId === finding.id;
  const actions = finding.actions?.length
    ? finding.actions
    : ([
        {
          id: 'explain',
          label: 'Explain Recommendation',
          prompt_mode: 'ask',
          prompt_template: `Explain this filing review finding: ${finding.title}. ${finding.reason}`,
          advisory_only: true,
        },
      ] as ReviewAction[]);

  return (
    <div
      className={`rounded-xl px-2.5 py-2 transition-colors ${
        isActive ? 'bg-[var(--counsel-hover-subtle)]' : 'hover:bg-[var(--counsel-hover-subtle)]'
      }`}
    >
      <button
        type="button"
        className={`flex w-full items-start gap-2 text-left ${PANEL_CAPTION}`}
        onClick={() => goToReviewFinding(finding)}
      >
        {severityDot(sev)}
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-[var(--counsel-text)]">{finding.title}</span>
          {finding.reason ? (
            <span className={`mt-0.5 block text-[11px] leading-snug ${PANEL_MUTED}`}>
              {finding.reason}
            </span>
          ) : null}
        </span>
      </button>
      <div className="mt-1.5 flex flex-wrap gap-1.5 pl-3.5">
        {actions.slice(0, 3).map((action) => (
          <button
            key={action.id}
            type="button"
            className="rounded-full border border-[var(--counsel-border)] px-2 py-0.5 text-[10px] text-[var(--counsel-text-muted)] hover:bg-[var(--counsel-hover-subtle)] hover:text-[var(--counsel-text)]"
            onClick={() => runFilingReviewFindingAction({ finding, action })}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function CategoryBlock({
  label,
  status,
  findings,
  defaultOpen,
}: {
  label: string;
  status: string;
  findings: FilingReviewFinding[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen) || status !== 'pass');
  return (
    <div className="border-b border-[var(--counsel-border)] last:border-b-0">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-1 py-2 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        {scorecardIcon(status)}
        <span className={`flex-1 text-[12px] font-medium ${PANEL_BODY}`}>{label}</span>
        <span className={`text-[10px] ${PANEL_MUTED}`}>{findings.length}</span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-[var(--counsel-text-muted)]" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-[var(--counsel-text-muted)]" />
        )}
      </button>
      {open ? (
        <div className="pb-2">
          {findings.length === 0 ? (
            <p className={`px-2 py-1 text-[11px] ${PANEL_MUTED}`}>No issues in this category.</p>
          ) : (
            findings.map((f) => <FindingRow key={f.id} finding={f} />)
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function FilingReviewScorecard() {
  const report = useLexCounselStore((s) => s.filingReviewReport);
  const loading = useLexCounselStore((s) => s.filingReviewLoading);
  const error = useLexCounselStore((s) => s.filingReviewError);
  const editorMode = useLexCounselStore((s) => s.editorCounselMode);
  const screen = useLexCounselStore((s) => s.workspace.screen);

  const findingsByCategory = useMemo(() => {
    const map = new Map<string, FilingReviewFinding[]>();
    if (!report) return map;
    for (const f of report.findings) {
      const cat = String(findingUxCategory(f));
      const list = map.get(cat) ?? [];
      list.push(f);
      map.set(cat, list);
    }
    return map;
  }, [report]);

  if (screen !== 'draft_editor' || editorMode !== 'review') return null;

  if (loading && !report) {
    return (
      <div className={`mx-3 mb-2 flex items-center gap-2 rounded-2xl border border-[var(--counsel-border)] px-3 py-2.5 ${PANEL_CAPTION}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Running filing review…
      </div>
    );
  }

  if (error && !report) {
    return (
      <div className={`mx-3 mb-2 rounded-2xl border border-rose-500/30 bg-rose-500/5 px-3 py-2.5 text-[12px] text-rose-700 dark:text-rose-300`}>
        {error}
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="mx-3 mb-2 max-h-[46vh] overflow-y-auto rounded-2xl border border-[var(--counsel-border)] bg-[var(--counsel-surface-elevated,transparent)]">
      <div className="sticky top-0 z-[1] border-b border-[var(--counsel-border)] bg-[var(--counsel-surface)] px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusBadgeClass(String(report.status))}`}
          >
            {filingStatusLabel(String(report.status))}
          </span>
          {typeof report.readinessScore === 'number' ? (
            <span className={`text-[11px] ${PANEL_MUTED}`}>{report.readinessScore}/100</span>
          ) : null}
          {report.confidence != null ? (
            <span className={`text-[11px] ${PANEL_MUTED}`}>
              Confidence {Math.round(Number(report.confidence) * 100)}%
            </span>
          ) : null}
        </div>
        <div className={`mt-1.5 text-[11px] ${riskClass(String(report.riskLevel ?? 'medium'))}`}>
          Litigation risk: {(report.riskLevel ?? 'medium').toString().toUpperCase()}
          {report.riskExplanation ? ` — ${report.riskExplanation}` : ''}
        </div>
        {report.packageLabel ? (
          <p className={`mt-1 text-[11px] ${PANEL_MUTED}`}>Package: {report.packageLabel}</p>
        ) : null}
        {report.executiveSummary?.length ? (
          <ul className={`mt-2 space-y-1 text-[11px] leading-snug ${PANEL_BODY}`}>
            {report.executiveSummary.slice(0, 4).map((line) => (
              <li key={line}>• {line}</li>
            ))}
          </ul>
        ) : null}
        {loading ? (
          <p className={`mt-1.5 flex items-center gap-1.5 text-[10px] ${PANEL_MUTED}`}>
            <Loader2 className="h-3 w-3 animate-spin" /> Refreshing…
          </p>
        ) : null}
      </div>

      <div className="px-2 py-1">
        {(report.scorecard?.length
          ? report.scorecard
          : [{ id: 'all', label: 'Findings', status: 'warn', finding_ids: report.findings.map((f) => f.id) }]
        ).map((card) => {
          const ids = card.finding_ids ?? card.findingIds ?? [];
          const findings =
            ids.length > 0
              ? report.findings.filter((f) => ids.includes(f.id))
              : findingsByCategory.get(String(card.id)) ?? [];
          return (
            <CategoryBlock
              key={String(card.id)}
              label={card.label}
              status={card.status}
              findings={findings}
              defaultOpen={card.status === 'fail'}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Narrow export for tests / reuse */
export type { FilingReviewReport };
